<script setup>
import { onMounted } from 'vue';
import { usePrimarySajuStore } from '@/store/modules/primarySaju';
import HarmonyPersonListPopup from '@/components/saju-insight/nangman-harmony/HarmonyPersonListPopup.vue';
import IconWand from './IconWand.vue';
import IconMagnifier from './IconMagnifier.vue';
import IconInfo from './IconInfo.vue';
import IconWrite from './IconWrite.vue';
import PopNamingFieldInput from './PopNamingFieldInput.vue';
import { NAMING_FIELD_CONFIGS } from './namingFieldConfigs';
import { addContentLogAPI } from '@/api/logServiceApi';

const useFunc = useFunctions();

const serviceTypes = [
  {
    key: 'recommend',
    iconComponent: IconWand,
    title: '이름 추천받기',
    desc: '새 이름, 별명, 브랜드명 등\n새로운 이름을 추천받고 싶은 경우',
    mobile: {
      title: '이름 추천',
      descLines: [
        { text: '이름, 브랜드명 등' },
        { emph: '새 이름 추천', text: ' 받고 싶어요' },
      ],
    },
  },
  {
    key: 'analyze',
    iconComponent: IconMagnifier,
    title: '기존 이름 분석하기',
    desc: '이미 있는 이름의 한자, 수리,\n점수, 의미를 분석하고 싶은 경우',
    mobile: {
      title: '이름 분석',
      descLines: [
        { emph: '기존 이름의 의미', text: '를' },
        { text: '분석하고 싶어요' },
      ],
    },
  },
];

import iconStore from '@/assets/images_manse/nangman-naming/icon-store.png';
import iconPerson from '@/assets/images_manse/nangman-naming/icon-person.png';

const targets = [
  { key: 'person', icon: null, img: iconPerson, label: '사람 이름', tone: 'person' },
  { key: 'pet', icon: 'mdi-paw', img: null, label: '반려 · 사물', tone: 'pet' },
  { key: 'biz', icon: null, img: iconStore, label: '사업 · 상호', tone: 'biz' },
  { key: 'culture', icon: 'mdi-pencil', img: null, label: '문화 · 아호', tone: 'culture' },
];

const targetColors = {
  person: '#e8ad41',
  pet: '#549d31',
  biz: '#4f7bbe',
  culture: '#6b3eaa',
};

/** 서비스 유형 + 대상 조합별 세부 옵션 (16가지 작명 모드) */
const detailsMap = {
  'recommend-person': [
    { key: 'kor', mark: '가', title: '한글이름추천', desc: '한글 이름 5개 추천' },
    { key: 'han', mark: '漢', title: '한글→한자 추천', desc: '한글 이름의 한자 변환 추천' },
  ],
  'analyze-person': [
    { key: 'kor', mark: '가', title: '한글이름분석', desc: '기존 한글 이름 점수 분석' },
    { key: 'han', mark: '漢', title: '한자이름분석', desc: '한자 이름 수리·획수 분석' },
  ],
  'recommend-pet': [
    { key: 'animal', mark: '동', title: '반려동물이름추천', desc: '보호자 사주 기반 동물 이름 5개 추천' },
    { key: 'plant', mark: '식', title: '반려식물이름추천', desc: '주인 사주 기반 식물 이름 5개 추천' },
    { key: 'object', mark: '물', title: '반려사물이름추천', desc: '자동차·노트북 등 사물 이름 추천' },
  ],
  'analyze-pet': [
    { key: 'animal', mark: '동', title: '반려동물이름분석', desc: '기존 반려동물 이름 점수 분석' },
    { key: 'plant', mark: '식', title: '반려식물이름분석', desc: '기존 식물 이름 점수 분석' },
    { key: 'object', mark: '물', title: '반려사물이름분석', desc: '기존 사물 이름 점수 분석' },
  ],
  'recommend-biz': [
    { key: 'biz', mark: '商', title: '상호추천', desc: '업종별 상호 5개 추천' },
  ],
  'analyze-biz': [
    { key: 'biz', mark: '商', title: '상호분석', desc: '기존 상호 점수 분석' },
  ],
  'recommend-culture': [
    { key: 'aho', mark: '雅', title: '아호추천', desc: '격국·용신 기반 한자 아호 5개 추천' },
    { key: 'nickname', mark: '닉', title: '닉네임추천', desc: '사주 기반 한글 자유 닉네임 5개 추천' },
  ],
  'analyze-culture': [
    { key: 'aho', mark: '雅', title: '아호분석', desc: '기존 한자 아호 점수 분석' },
    { key: 'nickname', mark: '닉', title: '닉네임분석', desc: '기존 닉네임 점수 분석' },
  ],
};

const props = defineProps({
  fixedSaju: { type: Object, default: null },
  /** 모바일 모드 — 폭/패딩/그리드 등 모바일 스타일 적용 */
  mobile: { type: Boolean, default: false },
});

const emit = defineEmits([
  'ready-change',
  'update:selected-type',
  'update:selected-target',
  'update:selected-detail',
  'update:detail-input',
  'update:selected-saju',
]);

const selectedType = ref(null);
const selectedTarget = ref(null);
const selectedDetail = ref(null);

const showDetailInputPopup = ref(false);
const detailInputResult = ref(null);

const details = computed(() => {
  if (!selectedType.value || !selectedTarget.value) return [];
  return detailsMap[`${selectedType.value}-${selectedTarget.value}`] || [];
});

/** 현재 선택 조합의 모드 키 */
const detailModeKey = computed(
  () => `${selectedType.value}-${selectedTarget.value}-${selectedDetail.value}`,
);

const detailInputFields = computed(() => NAMING_FIELD_CONFIGS[detailModeKey.value] || null);

const selectType = (key) => {
  addContentLogAPI({
    contentType: 'ap_naming_select_type_opt3',
    option3: key === 'recommend' ? '이름추천' : '이름분석',
  });
  selectedType.value = key;
};
const selectTarget = (key) => {
  addContentLogAPI({
    contentType: 'ap_naming_select_target_opt3',
    option3: targets.find((t) => t.key === key)?.label || key,
  });
  selectedTarget.value = key;
};
const selectDetail = (key) => {
  addContentLogAPI({
    contentType: 'ap_naming_select_detail_opt3',
    option3: details.value.find((d) => d.key === key)?.title || key,
  });
  // 같은 항목 재선택 시에는 입력값을 유지하면서 입력 팝업만 다시 연다
  if (selectedDetail.value === key) {
    showDetailInputPopup.value = true;
    return;
  }
  // 다른 detail로 전환 시 이전 입력값을 초기화하고 새 입력 팝업 오픈
  selectedDetail.value = key;
  detailInputResult.value = null;
  showDetailInputPopup.value = true;
};

/** 유형/대상 변경 시 세부 옵션·입력값 초기화 (옵션이 1개면 자동 선택) */
const resetDetail = () => {
  detailInputResult.value = null;
  if (details.value.length === 1) {
    selectedDetail.value = details.value[0].key;
  } else {
    selectedDetail.value = null;
  }
};
watch(selectedType, resetDetail);
watch(selectedTarget, resetDetail);

watch(selectedType, (v) => emit('update:selected-type', v));
watch(selectedTarget, (v) => emit('update:selected-target', v));
watch(selectedDetail, (v) => emit('update:selected-detail', v));
watch(detailInputResult, (v) => emit('update:detail-input', v));

const openDetailInputPopup = (e) => {
  e?.stopPropagation();
  showDetailInputPopup.value = true;
};

const handleDetailInputSubmit = (data) => {
  detailInputResult.value = data;
  showDetailInputPopup.value = false;
};

/** 작명 기준 사주 */
const selectedSaju = ref(props.fixedSaju);

const isReady = computed(() =>
  !!selectedType.value &&
  !!selectedTarget.value &&
  !!selectedDetail.value &&
  !!detailInputResult.value &&
  !!selectedSaju.value,
);

watch(isReady, (v) => {
  emit('ready-change', v);
}, { immediate: true });

/** 작명 기준 사주 — 내 사주 자동 로드 + 저장목록 팝업 연동 */
const primarySajuStore = usePrimarySajuStore();
const showSajuListPopup = ref(false);

const toPerson = (saju) => {
  if (!saju?.guestUserId) return null;
  const solarDate = saju.birthday || saju.solar || saju.birth || '';
  return {
    guestUserId: saju.guestUserId,
    guestUserName: saju.guestUserName || saju.name || '',
    name: saju.guestUserName || saju.name || '',
    gender: saju.gender,
    solar: solarDate,
    birth: saju.birth || solarDate,
    birthTime: saju.birthTime || saju.time || '',
  };
};

const formatSajuDate = (person) => {
  if (!person) return '';
  const raw = person.birth || person.solar || '';
  if (!raw) return '';
  if (typeof raw === 'string' && raw.includes('*')) return raw;
  return useFunc.formatCustomDate(raw);
};

const formatSajuTime = (person) => {
  if (!person) return '';
  if (person.birthTime) return person.birthTime;
  const solar = person.solar || '';
  const m = typeof solar === 'string' ? solar.match(/(\d{2}):(\d{2})/) : null;
  return m ? `${m[1]}:${m[2]}` : '';
};

const getGenderText = (g) => useFunc.getGenderTxt(g);

const openSajuListPopup = () => {
  if (props.fixedSaju) return;
  showSajuListPopup.value = true;
};

const handleSelectSaju = (person) => {
  if (props.fixedSaju) return;
  addContentLogAPI({
    contentType: 'ap_naming_select_saju_opt3',
    option3: person?.guestUserName || person?.name || '',
  });
  selectedSaju.value = person;
};

watch(selectedSaju, (v) => emit('update:selected-saju', v));

onMounted(async () => {
  if (props.fixedSaju) {
    selectedSaju.value = toPerson(props.fixedSaju);
    return;
  }
  if (!primarySajuStore.mySajuId) {
    try {
      await primarySajuStore.fetchMySaju();
    } catch (e) { /* ignore */ }
  }
  if (!selectedSaju.value) {
    selectedSaju.value = toPerson(primarySajuStore.mySaju);
  }
});

defineExpose({ canStart: isReady });
</script>

<template>
  <div class="si-overview__toc naming-toc" :class="{ mobile }">
    <h2 class="naming-toc__title">작명 서비스 선택</h2>

    <section class="naming-step">
      <div class="naming-step__head">
        <span class="naming-step__num">1</span>
        <span class="naming-step__title">서비스 유형 선택</span>
        <span class="naming-step__desc">원하는 서비스를 선택해주세요</span>
      </div>
      <div class="naming-cards">
        <button
          v-for="t in serviceTypes"
          :key="t.key"
          type="button"
          class="naming-card"
          :class="{ active: selectedType === t.key }"
          @click="selectType(t.key)"
        >
          <VIcon
            v-if="selectedType === t.key"
            icon="ri-checkbox-circle-fill"
            size="24"
            color="#724ce5"
            class="naming-card__check"
          />
          <template v-if="mobile">
            <span class="naming-card__header">
              <span class="naming-card__icon-wrap">
                <component :is="t.iconComponent" />
              </span>
              <span class="naming-card__title">{{ t.mobile.title }}</span>
            </span>
            <span class="naming-card__desc">
              <span
                v-for="(line, i) in t.mobile.descLines"
                :key="i"
                class="naming-card__desc-line"
              >
                <span v-if="line.emph" class="naming-card__desc-emph">{{ line.emph }}</span>{{ line.text }}
              </span>
            </span>
          </template>
          <template v-else>
            <span class="naming-card__icon-wrap">
              <component :is="t.iconComponent" />
            </span>
            <span class="naming-card__title">{{ t.title }}</span>
            <span class="naming-card__desc">{{ t.desc }}</span>
          </template>
        </button>
      </div>
    </section>

    <section class="naming-step">
      <div class="naming-step__head">
        <span class="naming-step__num">2</span>
        <span class="naming-step__title">대상 선택</span>
        <span class="naming-step__desc">서비스 대상을 선택해주세요.</span>
      </div>
      <div class="naming-targets">
        <button
          v-for="t in targets"
          :key="t.key"
          type="button"
          :class="['naming-target', `naming-target--${t.tone}`, { active: selectedTarget === t.key }]"
          @click="selectTarget(t.key)"
        >
          <span
            v-if="t.img"
            class="naming-target__img"
            :style="{ maskImage: `url(${t.img})`, WebkitMaskImage: `url(${t.img})` }"
          ></span>
          <VIcon
            v-else
            :icon="t.icon"
            size="15"
            :color="selectedTarget === t.key ? '#724ce5' : targetColors[t.key]"
          />
          <span class="naming-target__label">{{ t.label }}</span>
        </button>
      </div>

      <Transition name="naming-detail-fade">
        <div v-if="details.length" class="naming-detail">
          <div class="naming-detail__head">
            <VIcon icon="mdi-check" size="20" color="#724ce5" />
            <span class="naming-detail__title">세부 선택 사항</span>
          </div>
          <div class="naming-detail__list">
            <button
              v-for="(d, i) in details"
              :key="d.key"
              :style="{ '--i': i }"
              type="button"
              class="naming-detail__item"
              :class="{ active: selectedDetail === d.key }"
              @click="selectDetail(d.key)"
            >
              <span class="naming-detail__item-left">
                <span class="naming-detail__mark">{{ d.mark }}</span>
                <span class="naming-detail__text">
                  <span class="naming-detail__item-title">{{ d.title }}</span>
                  <span class="naming-detail__item-desc">{{ d.desc }}</span>
                </span>
              </span>
              <template v-if="selectedDetail === d.key">
                <span
                  v-if="detailInputResult"
                  class="naming-detail__done"
                  @click="openDetailInputPopup"
                >
                  <span class="naming-detail__done-inner">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12.5L10 17.5L19 7.5" stroke="#724CE5" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <span class="naming-detail__done-text">입력완료</span>
                  </span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 6L15 12L9 18" stroke="#A1A1A1" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </span>
                <span
                  v-else
                  class="naming-detail__action"
                  @click="openDetailInputPopup"
                >+ 입력하기</span>
              </template>
            </button>
          </div>
        </div>
      </Transition>

      <div class="naming-info">
        <IconInfo class="naming-info__icon" />
        <span class="naming-info__text">
          사주(용신)·오행을 보완하는 한글 이름을 추천드려요.<br />
          5개 규칙(용신 · 삼원오행 · 조후 · 입묘 · 공망)으로 100점 만점 평가됩니다.
        </span>
      </div>
    </section>

    <section class="naming-step">
      <div class="naming-step__head">
        <span class="naming-step__num">3</span>
        <span class="naming-step__title">작명 기준 사주</span>
        <span v-if="!fixedSaju" class="naming-step__desc">원하는 사주로도 변경할 수 있어요.</span>
      </div>
      <div class="naming-saju">
        <div v-if="selectedSaju" class="naming-saju__info">
          <span class="naming-saju__name">{{ selectedSaju.guestUserName || selectedSaju.name }}</span>
          <span class="naming-saju__meta">({{ getGenderText(selectedSaju.gender) }})</span>
          <span class="naming-saju__date">{{ formatSajuDate(selectedSaju) }}</span>
          <span class="naming-saju__time">{{ formatSajuTime(selectedSaju) }}</span>
        </div>
        <div v-else class="naming-saju__info naming-saju__info--empty">
          사주를 선택해주세요
        </div>
        <button v-if="!fixedSaju" type="button" class="naming-saju__edit" aria-label="편집" @click="openSajuListPopup">
          <IconWrite />
        </button>
      </div>
    </section>

    <HarmonyPersonListPopup
      v-if="!fixedSaju"
      :isVisible="showSajuListPopup"
      :excludeIds="[]"
      serviceType="naming"
      @close="showSajuListPopup = false"
      @select="handleSelectSaju"
    />

    <!-- 16개 모든 작명 모드 통합 입력 폼 (radio-text/check-buttons 포함 모든 필드 타입 지원) -->
    <PopNamingFieldInput
      v-if="detailInputFields"
      :fields="detailInputFields"
      :is-visible="showDetailInputPopup"
      :initial-data="detailInputResult"
      @close="showDetailInputPopup = false"
      @submit="handleDetailInputSubmit"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/nangman_style/_analysis_main_color.scss';

$naming-accent-bright: #714ce5;

.naming-toc {
  width: 100%;
  padding: 32px 40px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 24px;
  box-sizing: border-box;
  background: #fff;
  scrollbar-gutter: stable;
}

.naming-toc__title {
  width: 100%;
  margin: 0;
  text-align: center;
  color: #333;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
}

.naming-step {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  gap: 16px;
}

.naming-step__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
}

.naming-step__num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: $analysis-naming-bg;
  color: $naming-accent-bright;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
}

.naming-step__title {
  color: #333;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
}

.naming-step__desc {
  color: #333;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
}

/* 1단계: 서비스 유형 카드 (2-column grid) */
.naming-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 0 17.5px;
  width: 100%;
  box-sizing: border-box;
}

.naming-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 0;
  height: 170px;
  padding: 20px 15px;
  border-radius: 8px;
  border: 1px solid #ebebeb;
  background: #fff;
  cursor: pointer;
  text-align: center;
  transition: all 0.15s ease;

  &:hover {
    border-color: $naming-accent-bright;
  }

  &.active {
    border-color: $naming-accent-bright;
    background: rgba(155, 129, 238, 0.05);

    .naming-card__title {
      color: $naming-accent-bright;
    }
  }
}

.naming-card__check {
  position: absolute;
  top: 11px;
  right: 11px;
}

.naming-card__icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
}

.naming-card__title {
  color: #333;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  white-space: nowrap;
}

.naming-card__desc {
  color: #575757;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.3;
  white-space: pre-line;
}

/* 2단계: 대상 칩 (4-column) */
.naming-targets {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  padding: 0 17.5px;
  width: 100%;
  box-sizing: border-box;
}

.naming-target {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%;
  height: 50px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
  box-sizing: border-box;

  &__icon {
    font-size: 15px;
    flex-shrink: 0;
  }

  &__img {
    display: inline-block;
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    background-color: currentColor;
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
    -webkit-mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
  }

  &--person {
    background: rgba(255, 251, 243, 0.5);
    border-color: #ddcdac;
    color: #e8ad41;
  }

  &--pet {
    background: rgba(248, 255, 245, 0.5);
    border-color: #c0d7b5;
    color: #549d31;
  }

  &--biz {
    background: rgba(241, 246, 255, 0.5);
    border-color: #aec0db;
    color: #4f7bbe;
  }

  &--culture {
    background: rgba(242, 238, 255, 0.5);
    border-color: #beb4e2;
    color: #6b3eaa;
  }

  &.active {
    background: #f8f6fd;
    border-color: $naming-accent-bright;
    color: $naming-accent-bright;
    font-weight: 700;
  }
}

/* 어느 하나가 선택된 상태에서는 미선택 칩을 흐리게 */
.naming-targets:has(.active) .naming-target:not(.active) {
  opacity: 0.6;
}

/* 세부 선택 사항 */
.naming-detail {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0 17.5px;
  padding: 16px;
  border-radius: 10px;
  background: #f8f8f8;
  box-sizing: border-box;
  align-self: stretch;
}

.naming-detail__head {
  display: flex;
  align-items: center;
  gap: 5px;
}

.naming-detail__title {
  color: $naming-accent-bright;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
}

.naming-detail__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.naming-detail__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 15px 20px;
  border-radius: 8px;
  border: 1px solid #ebebeb;
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(113, 76, 229, 0.5);
  }

  &.active {
    border-color: $naming-accent-bright;
  }
}

.naming-detail__item-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.naming-detail__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 5px;
  background: #f8f6fd;
  color: $naming-accent-bright;
  font-family: Pretendard;
  font-size: 25px;
  font-weight: 700;
  line-height: 20px;
  flex-shrink: 0;
}

.naming-detail__text {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.naming-detail__item-title {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
}

.naming-detail__item-desc {
  color: #575757;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  word-break: keep-all;
}

/* 세부 선택 박스 — mypage/moreinfo 카드 펼침과 동일한 panel-slide 애니메이션 */
.naming-detail-fade-enter-active,
.naming-detail-fade-leave-active {
  transition: all 0.3s ease;
  max-height: 800px;
  overflow: hidden;
}
.naming-detail-fade-enter-from,
.naming-detail-fade-leave-to {
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  opacity: 0;
}

.naming-detail__action {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  flex-shrink: 0;
  cursor: pointer;
  padding: 4px 8px;
  transition: color 0.15s ease;

  &:hover {
    color: $naming-accent-bright;
  }
}

/* 카드 자체에 hover 시에도 + 입력하기 색상 동기화 */
.naming-detail__item:hover .naming-detail__action {
  color: $naming-accent-bright;
}

.naming-detail__done {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  cursor: pointer;

  &-inner {
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }

  &-text {
    color: $naming-accent-bright;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;
  }

  svg {
    flex-shrink: 0;
  }
}

/* 3단계: 작명 기준 사주 */
.naming-saju {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 0 17.5px;
  padding: 16px 24px;
  border-radius: 5px;
  background: #f8f8f8;
  box-sizing: border-box;
  align-self: stretch;
}

.naming-saju__info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  color: #333;
  font-family: Pretendard;
  font-size: 16px;
  line-height: 25px;
}

.naming-saju__name {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.naming-saju__meta,
.naming-saju__date,
.naming-saju__time {
  font-weight: 400;
  flex-shrink: 0;
  white-space: nowrap;
}

.naming-saju__edit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
}

/* 안내 박스 */
.naming-info {
  display: flex;
  gap: 10px;
  margin: 0 17.5px;
  padding: 16px;
  border-radius: 8px;
  background: $analysis-naming-bg;
  color: #333;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  box-sizing: border-box;
  align-self: stretch;

  &__icon {
    flex-shrink: 0;
  }

  &__text {
    flex: 1;
  }
}

/* ================== 모바일 오버라이드 ================== */
.naming-toc.mobile {
  /* PC 글로벌 스타일(.si-overview__toc — height/overflow/flex)이 모바일에 새어들어
     자체 영역만 스크롤되는 현상 차단. 모바일은 문서 레벨 스크롤이 정책 */
  height: auto;
  max-height: none;
  flex: none;
  overflow: visible;

  padding: 10px 16px 40px;
  gap: 30px;
  background: #fff;

  .naming-toc__title {
    display: none; /* 헤더가 외부에서 처리 */
  }

  .naming-step {
    gap: 15px;
  }

  .naming-step__head {
    padding: 0;
  }

  .naming-step__num {
    width: 24px;
    height: 24px;
    font-size: 16px;
  }

  .naming-step__title {
    font-size: 16px;
  }

  .naming-step__desc {
    font-size: 15px;
  }

  /* 1단계: 서비스 유형 카드 */
  .naming-cards {
    padding: 0;
    gap: 12px;
  }

  .naming-card {
    align-items: flex-start;
    height: auto;
    padding: 20px 15px;
    gap: 10px;
    text-align: left;
  }

  .naming-card__header {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  .naming-card__icon-wrap {
    width: 35px;
    height: 35px;
  }

  .naming-card__title {
    font-size: 16px;
  }

  .naming-card__desc {
    display: flex;
    flex-direction: column;
  }

  .naming-card__desc-line {
    color: #575757;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.4;
  }

  .naming-card__desc-emph {
    color: #2086d9;
    font-weight: 600;
  }

  /* 2단계: 대상 칩 — 2x2 그리드 */
  .naming-targets {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    padding: 0;
  }

  .naming-target {
    height: auto;
    padding: 15px;
    font-size: 14px;
  }

  /* 세부 선택 박스 */
  .naming-detail {
    margin: 0;
    padding: 16px;
  }

  .naming-detail__title {
    font-size: 16px;
  }

  .naming-detail__item {
    padding: 15px 12px;
  }

  /* 안내 박스 */
  .naming-info {
    margin: 0;
    padding: 16px;
  }

  /* 3단계: 작명 기준 사주 */
  .naming-saju {
    margin: 0;
    padding: 16px;
  }
}
</style>
